/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("OverflowBarWidgetBase", ["WidgetGroupBase", "WidgetFactory"], function (context, cls) {
  /**
   * Abstract base for a horizontal bar of items that automatically moves the items which don't fit
   * into an overflow popup behind a "…" trigger. It owns the whole overflow ENGINE (measurement,
   * single-pass partition, ResizeObserver-driven reflow, the trigger, the popup and focus management)
   * but carries NO ARIA pattern of its own: subclasses provide the semantic layer through the
   * `_barAriaRole` / `_popupRole` / `_overflowItemRole` seam —
   * e.g. ActionBar (toolbar + menu-button) or a future TabBar (tablist/tab).
   * Interactions go through the GBC widget pipeline (manageMouseClick / managePriorityKeyDown), never
   * through raw DOM listeners.
   * @class OverflowBarWidgetBase
   * @memberOf classes
   * @extends classes.WidgetGroupBase
   * @publicdoc Widgets
   */
  cls.OverflowBarWidgetBase = context.oo.Class(cls.WidgetGroupBase, function ($super) {
    return /** @lends classes.OverflowBarWidgetBase.prototype */ {
      __name: "OverflowBarWidgetBase",
      __virtual: true,

      $static: {
        /**
         * Pure partition: decides which items fit on one row and which overflow.
         * Single-pass, order-preserving, pinned items never overflow. No DOM access — unit-testable.
         * @param {Array.<{width: number, pinned: boolean}>} items - items in display order
         * @param {number} availableWidth - width of the bar
         * @param {number} triggerWidth - width of the "…" trigger (reserved only when overflowing)
         * @returns {{visible:number[], overflow:number[], showTrigger:boolean}} indices into items
         */
        distribute: function (items, availableWidth, triggerWidth) {
          let total = 0;
          for (const item of items) {
            total += item.width;
          }
          // Fast path: everything fits without needing the trigger.
          if (total <= availableWidth) {
            return { visible: items.map((_item, i) => i), overflow: [], showTrigger: false };
          }
          let pinnedWidth = 0;
          for (const item of items) {
            if (item.pinned) {
              pinnedWidth += item.width;
            }
          }
          const budget = availableWidth - triggerWidth - pinnedWidth;
          const visible = [];
          const overflow = [];
          let used = 0;
          let overflowing = false;
          items.forEach((item, i) => {
            if (item.pinned) {
              visible.push(i);
            } else if (!overflowing && used + item.width <= budget) {
              used += item.width;
              visible.push(i);
            } else {
              overflowing = true;
              overflow.push(i);
            }
          });
          return { visible, overflow, showTrigger: overflow.length > 0 };
        }
      },

      /** Emitted after a reflow changed the overflow set. @type {string} */
      overflowChange: "g_overflowChange",

      // ── Semantic seam — subclasses override these (defaults: neutral, no ARIA pattern) ──────────
      /** Container ARIA role, e.g. "toolbar" / "tablist". @type {?string} */
      _barAriaRole: null,
      /** Overflow popup role, e.g. "menu" / "listbox". Single source for the popup role, the trigger's
       * aria-haspopup (ARIA mandates they match) and the style class gbc_<WidgetName>__<role>. @type {?string} */
      _popupRole: null,
      /** ARIA role applied to items while inside the overflow popup, e.g. "menuitem". @type {?string} */
      _overflowItemRole: null,

      /** Tracked items in display order. @type {Array.<{widget: classes.WidgetBase, pinned: boolean, owned: boolean, order: number, clickHandle: ?Function}>} */
      _items: null,
      /** Widgets currently moved to the overflow popup. @type {classes.WidgetBase[]} */
      _overflowItems: null,
      /** Overflow popup. @type {classes.DropDownWidget} */
      _popup: null,
      /** The "…" trigger element. @type {HTMLElement} */
      _trigger: null,
      /** Bound "close the popup" handler reused for every item click registration. @type {?Function} */
      _itemClickHandler: null,
      /** Cached trigger width, invalidated on theme/font change. @type {number} */
      _triggerWidth: 0,
      /** True when the item set changed since the last reflow. @type {boolean} */
      _contentDirty: true,
      /** True when the container was resized since the last reflow. @type {boolean} */
      _sizeChanged: false,
      /** @type {?ResizeObserver} */
      _resizeObserver: null,
      /** Pending reflow animation frame id. @type {number} */
      _rafId: 0,

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        this._items = [];
        this._overflowItems = [];
        this._itemClickHandler = this._closePopup.bind(this);
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      _initContainerElement: function () {
        $super._initContainerElement.call(this);
        // Carry the base layout class on the root so the shared SCSS (flex sizing) applies even though
        // the root's own class is the concrete subclass (e.g. gbc_ActionBarWidget).
        this.addClasses("gbc_OverflowBarWidgetBase");
        this._trigger = this._element.getElementsByClassName(this.getBEMPartName("trigger"))[0];
        this._initPopup();
        if (this._barAriaRole) {
          this.setAriaRole(this._barAriaRole);
          this._element.setAttribute("aria-orientation", "horizontal");
        }
        if (this._popupRole) {
          this._trigger.setAttribute("aria-haspopup", this._popupRole);
        }
        this._trigger.setAttribute("aria-expanded", "false");
        // Element-scoped resize detection. ResizeObserver has no widget-pipeline equivalent (same
        // approach as ChromeActionService); a rAF breaks the observer's own re-entrancy.
        if (typeof ResizeObserver !== "undefined") {
          this._resizeObserver = new ResizeObserver(this._onResize.bind(this));
          this._resizeObserver.observe(this._element);
        }
      },

      /**
       * Create the overflow popup and apply the subclass popup semantics (role + style class).
       * Popup keyboard is handled in this widget's managePriorityKeyDown: an open DropDown registers its
       * PARENT (this bar) as KeyboardService's active dropdown, so priority keys route back here.
       * @private
       */
      _initPopup: function () {
        this._popup = cls.WidgetFactory.createWidget("DropDown", this.getBuildParameters());
        if (this._popupRole) {
          this._popup.setAriaRole(this._popupRole);
          // Variant style hook kept in sync with the role: gbc_<WidgetName>__<role> (e.g. gbc_ActionBarWidget__menu).
          this._popup.addClasses("gbc_" + this.__name + "__" + this._popupRole);
        }
        this._popup.reverseX = true;
        this._popup.setParentWidget(this);
        this._popup.shouldClose = function (target) {
          return !cls.DOMElementHelper.isElementOrChildOf(target, this._element);
        }.bind(this);
        this._popup.onClose(
          function () {
            this._trigger.setAttribute("aria-expanded", "false");
          }.bind(this)
        );
        const popupElement = this._popup.getElement();
        if (popupElement.id) {
          this._trigger.setAttribute("aria-controls", popupElement.id);
        }
      },

      /**
       * Add an item to the bar.
       * @param {classes.WidgetBase} widget - the item widget
       * @param {Object=} options - {pinned:boolean (never overflows), owned:boolean (destroyed by the bar), order:number}
       * @publicdoc
       */
      addItem: function (widget, options) {
        options = options || {};
        const entry = {
          widget: widget,
          pinned: Boolean(options.pinned),
          owned: Boolean(options.owned),
          order: cls.ObjectHelper.isNumber(options.order) ? options.order : this._items.length
        };
        this._items.push(entry);
        this._items.sort((a, b) => a.order - b.order);
        this._trackItem(entry);
        widget.getElement().classList.add(this.getBEMPartName("item"));
        this._containerElement.appendChild(widget.getElement());
        this.invalidate();
      },

      /**
       * Remove an item from the bar.
       * @param {classes.WidgetBase} widget - the item widget
       * @publicdoc
       */
      removeItem: function (widget) {
        const entry = this._items.find((item) => item.widget === widget);
        if (entry) {
          this._untrackItem(entry);
        }
        cls.ArrayHelper.removeMatching(this._items, (item) => item.widget === widget);
        cls.ArrayHelper.remove(this._overflowItems, widget);
        const element = widget.getElement();
        if (element) {
          element.classList.remove(this.getBEMPartName("item"));
          if (element.parentNode) {
            element.remove();
          }
        }
        // Owned items are the bar's responsibility; borrowed items belong to their VM controller.
        if (entry && entry.owned && !widget.isDestroyed()) {
          widget.destroy();
        }
        this.invalidate();
      },

      /**
       * Toggle whether an item may overflow.
       * @param {classes.WidgetBase} widget - the item widget
       * @param {boolean} pinned - true to keep it always visible
       */
      setItemPinned: function (widget, pinned) {
        const entry = this._items.find((item) => item.widget === widget);
        if (entry) {
          entry.pinned = Boolean(pinned);
          this.invalidate();
        }
      },

      /**
       * Replace the whole item set in one call (bulk form of addItem/removeItem). Items are displayed
       * in array order; elements of dropped items are detached. Borrowed by default (see addItem).
       * @param {Array.<{widget: classes.WidgetBase, pinned: boolean, owned: boolean}>} entries
       * @publicdoc
       */
      setItems: function (entries) {
        const nextWidgets = new Set(entries.map((entry) => entry.widget));
        for (const entry of this._items) {
          if (nextWidgets.has(entry.widget)) {
            continue;
          }
          this._untrackItem(entry);
          const element = entry.widget.getElement();
          if (element) {
            element.classList.remove(this.getBEMPartName("item"));
            if (element.parentNode) {
              element.remove();
            }
          }
          // Drop owned items entirely; borrowed ones are just released.
          if (entry.owned && !entry.widget.isDestroyed()) {
            entry.widget.destroy();
          }
        }
        const previous = new Map(this._items.map((entry) => [entry.widget, entry]));
        this._items = entries.map((entry, index) => {
          const kept = previous.get(entry.widget);
          const next = {
            widget: entry.widget,
            pinned: Boolean(entry.pinned),
            owned: Boolean(entry.owned),
            order: index,
            clickHandle: kept ? kept.clickHandle : null
          };
          if (!kept) {
            this._trackItem(next);
          }
          return next;
        });
        this._overflowItems.length = 0;
        this.invalidate();
      },

      /**
       * Register the "click closes the popup" hook on an item (GBC event system, no DOM listener).
       * @param {Object} entry
       * @private
       */
      _trackItem: function (entry) {
        entry.clickHandle = entry.widget.when(context.constants.widgetEvents.click, this._itemClickHandler);
      },

      /**
       * Unregister the item click hook.
       * @param {Object} entry
       * @private
       */
      _untrackItem: function (entry) {
        if (entry.clickHandle) {
          entry.clickHandle();
          entry.clickHandle = null;
        }
      },

      /** @returns {classes.WidgetBase[]} items currently in the overflow popup */
      getOverflowItems: function () {
        return this._overflowItems.slice();
      },

      /** @returns {boolean} true when at least one item is in overflow */
      hasOverflow: function () {
        return this._overflowItems.length > 0;
      },

      /**
       * Mark the bar dirty and schedule a reflow (coalesced to one per animation frame).
       * @publicdoc
       */
      invalidate: function () {
        this._contentDirty = true;
        this._schedule();
      },

      /**
       * Invalidate the cached trigger width (call on theme/font/density change).
       */
      invalidateTriggerWidth: function () {
        this._triggerWidth = 0;
      },

      /**
       * Schedule a reflow on the next animation frame, collapsing bursts to a single call.
       * @private
       */
      _schedule: function () {
        if (this._rafId) {
          window.cancelAnimationFrame(this._rafId);
        }
        this._rafId = window.requestAnimationFrame(
          function () {
            this._rafId = 0;
            this.reflow();
          }.bind(this)
        );
      },

      /**
       * ResizeObserver callback: the container changed size, so the next reflow must recompute.
       * @private
       */
      _onResize: function () {
        this._sizeChanged = true;
        this._schedule();
      },

      /**
       * Recompute the partition and apply it. Strict read→decide→mutate to avoid layout thrashing.
       * @publicdoc
       */
      reflow: function () {
        if (!this._element?.isConnected) {
          return;
        }
        // Dirty-gate: skip only when nothing requested a reflow (no content change and no resize).
        // A resize always recomputes: comparing widths here would wrongly skip on a stale read.
        if (!this._contentDirty && !this._sizeChanged) {
          return;
        }
        // Capture focus before any DOM move: re-attaching or removing items can blur the focused item.
        const priorFocus = document.activeElement;
        const restoreFocus = this._element.contains(priorFocus);
        if (this._popup.isVisible()) {
          this._popup.hide();
        }
        this._reattachAll();
        this._trigger.hidden = false;
        // READ phase (single forced layout): all widths read together, before any mutation.
        const available = this._element.clientWidth;
        const triggerWidth = this._measureTrigger();
        const visibleEntries = this._items.filter((entry) => entry.widget.isVisible());
        const measured = visibleEntries.map((entry) => ({ width: this._outerWidth(entry.widget.getElement()), pinned: entry.pinned }));
        // DECIDE phase (pure arithmetic).
        const plan = cls.OverflowBarWidgetBase.distribute(measured, available, triggerWidth);
        // MUTATE phase.
        this._overflowItems = plan.overflow.map((i) => visibleEntries[i].widget);
        for (const widget of this._overflowItems) {
          widget.getElement().remove();
        }
        this._trigger.hidden = !plan.showTrigger;
        // Restore focus if our DOM changes blurred the previously-focused bar element: re-focus it if it
        // is still in the bar, otherwise fall back to the always-reachable trigger so focus never drops
        // to <body>.
        if (restoreFocus && !this._element.contains(document.activeElement)) {
          if (priorFocus.isConnected && this._element.contains(priorFocus)) {
            priorFocus.focus();
          } else if (!this._trigger.hidden) {
            this._trigger.focus();
          }
        }
        this._contentDirty = false;
        this._sizeChanged = false;
        this.emit(this.overflowChange, { overflow: this._overflowItems.slice(), overflowing: this.hasOverflow() });
      },

      /**
       * Bring every item element back into the bar in order, clearing any leftover popup semantics,
       * so all items are measurable in the read phase.
       * @private
       */
      _reattachAll: function () {
        for (const entry of this._items) {
          const element = entry.widget.getElement();
          element.classList.add(this.getBEMPartName("item"));
          element.removeAttribute("role");
          element.removeAttribute("aria-posinset");
          element.removeAttribute("aria-setsize");
          this._containerElement.appendChild(element);
        }
      },

      /**
       * Full horizontal footprint of an element: border-box width + horizontal margins. distribute must
       * reserve the real laid-out space — items carry margins, so measuring bare offsetWidth under-counts
       * and keeps one item too many visible (it then clips against the overflow:hidden row).
       * @param {HTMLElement} element
       * @returns {number}
       * @private
       */
      _outerWidth: function (element) {
        const style = window.getComputedStyle(element);
        return element.offsetWidth + (Number.parseFloat(style.marginLeft) || 0) + (Number.parseFloat(style.marginRight) || 0);
      },

      /**
       * Measure (and cache) the trigger footprint (width + margins). The trigger must be visible when first measured.
       * @returns {number}
       * @private
       */
      _measureTrigger: function () {
        if (!this._triggerWidth) {
          this._triggerWidth = this._outerWidth(this._trigger);
        }
        return this._triggerWidth;
      },

      /**
       * Open the overflow popup: move overflow items into it, apply the subclass item role, focus first.
       * @private
       */
      _openPopup: function () {
        if (this._overflowItems.length === 0) {
          return;
        }
        const container = this._popup.getContainerElement();
        this._overflowItems.forEach((widget, index, all) => {
          const element = widget.getElement();
          if (this._overflowItemRole) {
            element.setAttribute("role", this._overflowItemRole);
          }
          element.setAttribute("tabindex", "-1");
          element.setAttribute("aria-posinset", String(index + 1));
          element.setAttribute("aria-setsize", String(all.length));
          container.appendChild(element);
        });
        this._popup.show();
        this._trigger.setAttribute("aria-expanded", "true");
        const first = container.getElementsByClassName(this.getBEMPartName("item"))[0];
        if (first) {
          first.focus();
        }
      },

      /**
       * Close the overflow popup and return focus to the trigger. Items are reclaimed on the next reflow.
       * @private
       */
      _closePopup: function () {
        if (!this._popup.isVisible()) {
          return;
        }
        this._popup.hide();
        if (this._trigger.isConnected) {
          this._trigger.focus();
        }
      },

      /**
       * Popup keyboard navigation (routed via the open popup's managePriorityKeyDown): Up/Down/Home/End
       * move focus between popup items, Enter/Space activate the focused item, Escape closes and returns
       * focus to the trigger.
       * Owned here, not delegated to ChoiceDropDownWidget: that base marks the current item with a CSS
       * class only (no roving focus / aria-activedescendant → screen readers don't follow it), whereas
       * moving real DOM focus per item is the accessible APG menu pattern. Keeping nav here also leaves
       * the generic _popupRole seam (menu / future listbox) and the borrowed host-owned items free.
       * @param {string} keyString - normalized key
       * @returns {boolean} true if the key was handled
       * @private
       */
      _handlePopupKey: function (keyString) {
        const items = Array.from(this._popup.getContainerElement().getElementsByClassName(this.getBEMPartName("item")));
        if (items.length === 0) {
          return false;
        }
        const index = items.indexOf(document.activeElement);
        switch (keyString) {
          case "down":
            items[(index + 1 + items.length) % items.length].focus();
            return true;
          case "up":
            items[(index - 1 + items.length) % items.length].focus();
            return true;
          case "home":
            items[0].focus();
            return true;
          case "end":
            items.at(-1).focus();
            return true;
          case "enter":
          case "return":
          case "space":
            // Activate the focused item through the click pipeline (runs its action; the bound click
            // handler then closes the popup). Works for any element, not only native buttons.
            if (index >= 0) {
              items[index].click();
            }
            return true;
          case "esc":
            this._closePopup();
            return true;
          default:
            return false;
        }
      },

      /**
       * Toggle the overflow popup when the "…" trigger is clicked (the click bubbles up to the bar).
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        const target = domEvent?.target;
        if (target?.closest?.("." + this.getBEMPartName("trigger"))) {
          if (this._popup.isVisible()) {
            this._closePopup();
          } else {
            this._openPopup();
          }
          // Consume the trigger click: do not bubble to parent widgets (which could request VM focus
          // and steal it from the menu item we just focused).
          return false;
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * Menu-button keyboard. While the popup is open KeyboardService routes the active-dropdown keys to
       * this widget (the popup registers its PARENT as the active dropdown), so we navigate the popup
       * here; while it is closed, the arrow keys open it when the "…" trigger is focused.
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this._popup.isVisible()) {
          if (this._handlePopupKey(keyString)) {
            return true;
          }
        } else if (
          document.activeElement === this._trigger &&
          (keyString === "down" || keyString === "up" || this._isActivationKey(keyString, repeat))
        ) {
          // Down/Up per APG menu-button, plus Enter/Space for plain activation
          this._openPopup();
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._rafId) {
          window.cancelAnimationFrame(this._rafId);
          this._rafId = 0;
        }
        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }
        if (this._popup) {
          this._popup.destroy();
          this._popup = null;
        }
        for (const entry of this._items) {
          this._untrackItem(entry);
          // Owned items are the bar's responsibility; borrowed items belong to their VM controller.
          if (entry.owned && !entry.widget.isDestroyed()) {
            entry.widget.destroy();
          }
        }
        this._items.length = 0;
        this._overflowItems.length = 0;
        $super.destroy.call(this);
      }
    };
  });
});
